"use client";

import { useEffect, useRef } from "react";
import { useMotionMode } from "@/context/MotionContext";
import type Lenis from "lenis";

interface SmoothScrollOptions {
  scrollToTopOnInit?: boolean;
}

export function useSmoothScroll(options: SmoothScrollOptions = {}) {
  const { scrollToTopOnInit = false } = options;
  const { canAnimate, mounted } = useMotionMode();
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    // Wait for MotionContext to determine animation capability
    if (!mounted) return;

    // Skip Lenis on mobile - native touch scroll works better
    const isMobile = window.innerWidth < 768 || "ontouchstart" in window;

    // If animations are disabled (reduced motion or low GPU), use native scroll
    if (!canAnimate) {
      if (scrollToTopOnInit) {
        window.scrollTo(0, 0);
      }
      // Still register ScrollTrigger for static positioning, but without animations
      const initBasicScrollTrigger = async () => {
        const gsap = (await import("gsap")).default;
        const { ScrollTrigger } = await import("gsap/ScrollTrigger");
        gsap.registerPlugin(ScrollTrigger);
        ScrollTrigger.config({ ignoreMobileResize: true });
      };
      initBasicScrollTrigger();
      return;
    }

    // Initialize scroll system with animations
    const initScrollTrigger = async () => {
      const gsap = (await import("gsap")).default;
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");
      gsap.registerPlugin(ScrollTrigger);

      // Performance: ignore mobile resize events that trigger on scroll (address bar hide/show)
      ScrollTrigger.config({ ignoreMobileResize: true });

      if (isMobile) {
        // On mobile, just use native scroll - no Lenis needed
        if (scrollToTopOnInit) {
          window.scrollTo(0, 0);
        }
        return;
      }

      // Desktop: use Lenis smooth scroll (dynamic import)
      const { default: LenisClass } = await import("lenis");

      // Scroll to top before Lenis reads initial position (if requested)
      if (scrollToTopOnInit) {
        window.scrollTo(0, 0);
      }

      const lenis = new LenisClass({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: "vertical",
        gestureOrientation: "vertical",
        smoothWheel: true,
      });

      lenisRef.current = lenis;

      // Expose Lenis on window for phase navigation to control
      (window as Window & { __lenis?: Lenis }).__lenis = lenis;

      // Use scrollerProxy to properly integrate Lenis with ScrollTrigger
      // This ensures they share the same scroll position and RAF tick
      ScrollTrigger.scrollerProxy(document.body, {
        scrollTop(value?: number) {
          if (arguments.length && value !== undefined) {
            lenis.scrollTo(value, { immediate: true });
          }
          return lenis.scroll;
        },
        getBoundingClientRect() {
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight,
          };
        },
      });

      // Single RAF loop: GSAP ticker drives Lenis, Lenis updates trigger ScrollTrigger.update
      // This avoids the double-RAF problem of having separate update loops
      gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
      });

      // Update ScrollTrigger after Lenis processes scroll
      lenis.on("scroll", ScrollTrigger.update);

      // Disable GSAP's default lag smoothing to match Lenis
      gsap.ticker.lagSmoothing(0);

      // Refresh ScrollTrigger after proxy setup
      ScrollTrigger.refresh();
    };

    initScrollTrigger();

    return () => {
      lenisRef.current?.destroy();
    };
  }, [scrollToTopOnInit, canAnimate, mounted]);

  return lenisRef;
}
